import React, { useEffect, useState } from "react";
import axios from "axios";
import { Play, Lock, Disc3, ExternalLink, Loader2 } from "lucide-react";
import { useVideo } from "./VideoContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TrackCard({ track, index }) {
  const { open } = useVideo();
  const [hover, setHover] = useState(false);
  const num = String(index + 1).padStart(2, "0");
  const hasVideo = Boolean(track.youtube_id);
  const inStudio = !hasVideo && track.status === "in_studio";
  const bandcampUrl = track.bandcamp_url;

  const onPlay = (e) => {
    e?.stopPropagation?.();
    if (hasVideo) open(track.youtube_id);
  };

  return (
    <article
      data-testid={`track-card-${track.id}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onPlay}
      className={`glass-card group relative p-6 md:p-7 ${hasVideo ? "cursor-pointer hover:border-[#FF00E5]/50" : ""}`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <div className="absolute -top-2 -right-2 font-display font-black text-7xl md:text-8xl leading-none text-white/[0.04] select-none pointer-events-none">
        {num}
      </div>

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <span className="font-mono-display text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">
            Track {num}
          </span>
          <span
            className={`font-mono-display text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border ${
              hasVideo
                ? "border-[#FF00E5] text-[#FF00E5] glow-magenta"
                : inStudio
                ? "border-[#00F0FF] text-[#00F0FF] glow-cyan"
                : "border-[#FF00E5]/40 text-[#FF00E5]/90"
            }`}
          >
            {hasVideo ? "Video Out" : inStudio ? "In The Studio" : track.tag || "Coming Soon"}
          </span>
        </div>

        <h3 className="font-display font-bold text-xl md:text-2xl text-cream-soft group-hover:text-glow-cyan transition-all leading-tight">
          {track.title}
        </h3>

        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            data-testid={`track-play-${track.id}`}
            aria-label={hasVideo ? `Play ${track.title} video` : `Preview ${track.title} — coming soon`}
            disabled={!hasVideo}
            onClick={onPlay}
            className={`relative flex items-center gap-3 ${
              hasVideo ? "text-[#FF00E5] hover:text-[#00F0FF] transition-colors" : "text-white/40 cursor-not-allowed"
            }`}
          >
            <span
              className={`relative inline-flex items-center justify-center w-11 h-11 rounded-full border ${
                hasVideo
                  ? "border-[#FF00E5]/50 bg-black/60 group-hover:border-[#FF00E5] group-hover:scale-110 group-hover:glow-magenta"
                  : "border-white/15 bg-black/60"
              } transition-all`}
            >
              {hasVideo ? <Play size={14} className="ml-0.5 fill-current" /> : hover ? <Lock size={14} /> : <Play size={14} className="ml-0.5" />}
            </span>
            <span className="font-mono-display text-[11px] uppercase tracking-[0.25em]">
              {hasVideo ? "Watch" : hover ? "Locked" : "Preview"}
            </span>
          </button>
          <span className="font-mono-display text-[11px] uppercase tracking-[0.25em] text-white/35">
            {hasVideo ? "YouTube" : inStudio ? "Mixing" : "Soon"}
          </span>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-mono-display uppercase tracking-[0.25em] text-white/40">
            <Disc3 size={11} className={inStudio ? "text-[#00F0FF] animate-spin" : "text-[#FF00E5]"} style={inStudio ? { animationDuration: "4s" } : {}} />{" "}
            {hasVideo ? "Out Now · Album 2026" : inStudio ? "Mixing · Coming 2026" : "Coming 2026"}
          </div>
          {bandcampUrl && (
            <a
              data-testid={`track-bandcamp-${track.id}`}
              href={bandcampUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 hover:glow-cyan transition-all text-[10px] font-mono-display uppercase tracking-[0.2em]"
            >
              Bandcamp <ExternalLink size={9} />
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

export default function Tracks() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/tracks`)
      .then((r) => setTracks(r.data.tracks || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="tracks" data-testid="tracks-section" className="relative py-24 md:py-32 px-5 md:px-10 max-w-7xl mx-auto">
      <div className="text-center mb-14 md:mb-20 reveal">
        <span className="font-mono-display text-[11px] uppercase tracking-[0.4em] text-[#00F0FF]">
          01 — The Album
        </span>
        <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl mt-3 text-gradient-neon">
          PRE-RELEASE
        </h2>
        <p className="mt-4 max-w-xl mx-auto text-white/65 text-sm md:text-base">
          Seven tracks. One concept. A cinematic descent into sound — releasing 2026.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-white/55 font-mono-display text-xs uppercase tracking-[0.3em]">
          <Loader2 size={16} className="animate-spin mr-3" /> Loading tracks…
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {tracks.map((t, i) => (
            <TrackCard key={t.id} track={t} index={i} />
          ))}
        </div>
      )}    </section>
  );
}
